import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasAnyPermissionPath } from "./authorization/authorization";
import { signOut } from "./features/authentication/authenticationSlice";

/**
 * Route guard: show children only if user has required permission(s).
 * Direct URL access without permission → warning dialog; OK logs the user out.
 */
const PermissionRoute = ({ children, requiredPermissions = [] }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissions } = useSelector((state) => state.auth);

  const hasPermission = hasAnyPermissionPath(
    permissions || [],
    requiredPermissions,
  );

  const handleLogout = () => {
    dispatch(signOut());
    navigate("/", { replace: true });
  };

  if (hasPermission) {
    return children;
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        handleLogout();
      }}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 10000 }}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          px: 1,
          py: 0.5,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(245, 158, 11, 0.15)",
            color: "#d97706",
            flexShrink: 0,
          }}
        >
          <WarningAmberRoundedIcon />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
          Access Denied
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "#475569", fontSize: "0.95rem", lineHeight: 1.5 }}>
          You do not have permission to access this page. You will be logged out.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          onClick={handleLogout}
          autoFocus
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            minWidth: 88,
            background: "#0f2744",
            "&:hover": { background: "#163a5f" },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionRoute;
